import React, { useCallback, useEffect, useRef, useState } from 'react';
import { setVMTDataFromTransportation } from '../../services/vmtDataService';
import { useLocation } from '../../contexts/LocationContext';
import { useChartPngRegistryActions } from '../../contexts/ChartPngRegistryContext';
import { useAdminPageSettings } from '../../hooks/useAdminPageSettings';
import { ChartLayout, EquityContent, MethodologyContent, OverviewContent, RelevanceContent } from '../charts/ChartLayout';
import EVChargingInfrastructure from '../charts/EVChargingInfrastructure';
import EVFleetAdoption from '../charts/EVFleetAdoption';
import { KeyMetric, KeyMetrics } from '../charts/KeyMetrics';
import { VMTChart, VMTMetrics } from '../charts/VMTChart';
import { VMTComparisonChart } from '../charts/VMTComparisonChart';
import { EMFACCountyVMTChart } from '../charts/EMFACCountyVMTChart';
import { EMFACFleetComparisonChart } from '../charts/EMFACFleetComparisonChart';
import { PageHeader } from '../layout/PageHeader';
import EVAdoptionBarriersChart from '../charts/EVAdoptionBarriersChart';
import MeansOfTransportation from '../charts/MeansOfTransportation';
import GenericMapTiered from '../charts/GenericMapTiered';
import { CustomSectionsForPage } from '../customContent/CustomSectionsForPage';
import { CustomDatasetsForPage } from '../customContent/CustomDatasetsForPage';


interface TransportationProps {
  location?: string;
}

interface TransportMapMetrics {
  currentValue: number;
  californiaAverage: number;
  year: number;
  variable: string;
  minValue?: number;
  maxValue?: number;
}

export const Transportation: React.FC<TransportationProps> = ({ location: propLocation }) => {
  const { selectedLocation, selectedGeofips, coreDataLoading } = useLocation();
  const [activeVMTView, setActiveVMTView] = useState<string>('overview');
  const [activeEVView, setActiveEVView] = useState<string>('overview');
  const [activeChargingView, setActiveChargingView] = useState<string>('overview');
  const [vmtChartView, setVmtChartView] = useState<string>('total');
  const [vmtMetrics, setVmtMetrics] = useState<VMTMetrics | null>(null);
  // VMT per capita metrics removed - no longer needed
  const [evOverview, setEvOverview] = useState<{ observedYear: number; observedTotal: number; projected2030: number; target2030: number; onPace: boolean } | null>(null);
  const [evChargingView, setEvChargingView] = useState<'counts' | 'peoplePerCharger' | 'evsPerCharger' | 'map'>('peoplePerCharger');
  const [evAdoptionView, setEvAdoptionView] = useState<'perCapita' | 'projection' | 'map'>('projection');
  const [vmtDataTableContent, setVmtDataTableContent] = useState<React.ReactNode>(null);
  const [vmtShowDataTable, setVmtShowDataTable] = useState(false);
  const [vmtDownloadPNG, setVmtDownloadPNG] = useState<(() => void) | null>(null);
  const [vmtDownloadCSV, setVmtDownloadCSV] = useState<(() => void) | null>(null);
  const [comparisonDownloadPNG, setComparisonDownloadPNG] = useState<(() => void) | null>(null);
  const [emfacCountyDownloadPNG, setEmfacCountyDownloadPNG] = useState<(() => void) | null>(null);
  const [emfacFleetDownloadPNG, setEmfacFleetDownloadPNG] = useState<(() => void) | null>(null);
  const [transportMapMetrics, setTransportMapMetrics] = useState<TransportMapMetrics | null>(null);
  const [activeTransportMapView, setActiveTransportMapView] = useState<'overview' | 'relevance' | 'equity' | 'methodology'>('overview');
  // State for VMT backcasting options
  const [vmt2005_2014Method, setVmt2005_2014Method] = useState<'EMFAC2025' | 'Population' | 'Combined'>('Combined');
  const [vmtBackcastingMethod, setVmtBackcastingMethod] = useState<'AADT' | 'EMFAC2025' | 'EMFAC Fleet' | 'EMFAC2021 Fleet' | 'Population' | 'Combined'>('Combined');

  const vmtDownloadPNGRef = useRef<(() => void) | null>(null);
  const comparisonDownloadPNGRef = useRef<(() => void) | null>(null);
  const emfacCountyDownloadPNGRef = useRef<(() => void) | null>(null);
  const emfacFleetDownloadPNGRef = useRef<(() => void) | null>(null);

  useEffect(() => {
    vmtDownloadPNGRef.current = vmtDownloadPNG;
  }, [vmtDownloadPNG]);
  useEffect(() => {
    comparisonDownloadPNGRef.current = comparisonDownloadPNG;
  }, [comparisonDownloadPNG]);
  useEffect(() => {
    emfacCountyDownloadPNGRef.current = emfacCountyDownloadPNG;
  }, [emfacCountyDownloadPNG]);
  useEffect(() => {
    emfacFleetDownloadPNGRef.current = emfacFleetDownloadPNG;
  }, [emfacFleetDownloadPNG]);

  const currentLocation = propLocation || selectedLocation;
  const { register: registerPng, unregister: unregisterPng } = useChartPngRegistryActions();

  // Register every figure (each chart view) so the download modal lists all of them.
  // Deferred so registration runs after first paint and does not block load; stable deps to avoid re-run loops.
  useEffect(() => {
    if (!currentLocation) return;
    let cancelled = false;
    const t = setTimeout(() => {
      if (cancelled) return;
      const loc = currentLocation.replace(/\s+/g, '_');
      const delay = 600;
      const unreg: string[] = [];

      // VMT (5 views) - each switches view then runs that view's export after delay
      registerPng('VMT_Total', 'Vehicle Miles Traveled (Total VMT)', () => {
      setVmtChartView('total');
      setTimeout(() => vmtDownloadPNGRef.current?.(), delay);
    });
    unreg.push('VMT_Total');
    registerPng('VMT_PerCapita', 'Vehicle Miles Traveled (Per Capita)', () => {
      setVmtChartView('percapita');
      setTimeout(() => vmtDownloadPNGRef.current?.(), delay);
    });
    unreg.push('VMT_PerCapita');
    registerPng('VMT_Comparison', 'VMT Comparison (Light-Duty vs Heavy-Duty)', () => {
      setVmtChartView('comparison');
      setTimeout(() => comparisonDownloadPNGRef.current?.(), delay);
    });
    unreg.push('VMT_Comparison');
    registerPng('VMT_EMFAC_County', 'EMFAC County LDV VMT', () => {
      setVmtChartView('emfac');
      setTimeout(() => emfacCountyDownloadPNGRef.current?.(), delay);
    });
    unreg.push('VMT_EMFAC_County');
    registerPng('VMT_EMFAC_Fleet', 'EMFAC Fleet LDV VMT', () => {
      setVmtChartView('fleet');
      setTimeout(() => emfacFleetDownloadPNGRef.current?.(), delay);
    });
    unreg.push('VMT_EMFAC_Fleet');

    // EV Adoption (3 views) - switch view then export canvas
    const evAdoptionViews = [
      { id: 'EV_Adoption_Projection', label: 'Electric Vehicle Adoption (Projection to 2030)', view: 'projection' as const },
      { id: 'EV_Adoption_PerCapita', label: 'Electric Vehicle Adoption (Per 1,000 residents)', view: 'perCapita' as const },
      { id: 'EV_Adoption_Map', label: 'Electric Vehicle Adoption (Tract Map)', view: 'map' as const }
    ];
    evAdoptionViews.forEach(({ id, label, view }) => {
      registerPng(id, label, () => {
        setEvAdoptionView(view);
        setTimeout(() => {
          const canvas = document.querySelector('#ev-adoption-chart canvas') as HTMLCanvasElement;
          if (canvas) {
            const link = document.createElement('a');
            link.download = `EV_Adoption_${view === 'projection' ? 'Projection' : view === 'perCapita' ? 'Per_Capita' : 'Tract_Map'}_${loc}.png`;
            link.href = canvas.toDataURL('image/png');
            link.click();
          }
        }, delay);
      });
      unreg.push(id);
    });

    // EV Charging (4 views)
    const evChargingViews = [
      { id: 'EV_Charging_People', label: 'EV Charging (People per Charger)', view: 'peoplePerCharger' as const },
      { id: 'EV_Charging_Counts', label: 'EV Charging (Total Chargers)', view: 'counts' as const },
      { id: 'EV_Charging_EVs', label: 'EV Charging (EVs per Charger)', view: 'evsPerCharger' as const },
      { id: 'EV_Charging_Map', label: 'EV Charging (Tract Map)', view: 'map' as const }
    ];
    evChargingViews.forEach(({ id, label, view }) => {
      registerPng(id, label, () => {
        setEvChargingView(view);
        setTimeout(() => {
          const canvas = document.querySelector('#ev-charging-chart canvas') as HTMLCanvasElement;
          if (canvas) {
            const link = document.createElement('a');
            const viewLabel = view === 'counts' ? 'Total_Chargers' : view === 'peoplePerCharger' ? 'People_Per_Charger' : view === 'evsPerCharger' ? 'EVs_Per_Charger' : 'Tract_Map';
            link.download = `EV_Charging_${viewLabel}_${loc}.png`;
            link.href = canvas.toDataURL('image/png');
            link.click();
          }
        }, delay);
      });
      unreg.push(id);
    });

    // Transportation-Related Maps (1 - current map variable)
    registerPng('Transportation_Maps', 'Transportation-Related Maps', () => {
      const container = document.querySelector('#transportation-maps-chart');
      const canvas = container?.querySelector('canvas');
      if (canvas) {
        const link = document.createElement('a');
        link.download = `Transportation_Maps_${loc}.png`;
        link.href = (canvas as HTMLCanvasElement).toDataURL('image/png');
        link.click();
      }
    });
        unreg.push('Transportation_Maps');
    }, 0);

    return () => {
      cancelled = true;
      clearTimeout(t);
      // Unregister all in case we unmount before timeout runs
      ['VMT_Total', 'VMT_PerCapita', 'VMT_Comparison', 'VMT_EMFAC_County', 'VMT_EMFAC_Fleet',
       'EV_Adoption_Projection', 'EV_Adoption_PerCapita', 'EV_Adoption_Map',
       'EV_Charging_People', 'EV_Charging_Counts', 'EV_Charging_EVs', 'EV_Charging_Map',
       'Transportation_Maps'].forEach((id) => unregisterPng(id));
    };
  }, [currentLocation, registerPng, unregisterPng]);
  const {
    isSectionEnabled,
    isChartEnabled,
    getSectionOrder,
    getChartOrder
  } = useAdminPageSettings('transportation', currentLocation);

  const vmtOptions = [
    { id: 'vehicle_miles', value: 'total', label: 'Total VMT' },
    { id: 'vmt_percapita', value: 'percapita', label: 'Per Capita' },
    { id: 'vmt_comparison', value: 'comparison', label: 'VMT Comparison' },
    { id: 'vmt_emfac', value: 'emfac', label: 'EMFAC' },
    { id: 'vmt_fleet', value: 'fleet', label: 'Fleet' }
  ];
  const allowedVmtOptions = vmtOptions
    .filter((option) => isChartEnabled('vmt', option.id))
    .sort((a, b) => getChartOrder('vmt', a.id) - getChartOrder('vmt', b.id));

  const evAdoptionOptions = [
    { id: 'ev_fleet_adoption', value: 'projection', label: 'Projection to 2030' },
    { id: 'ev_per_capita', value: 'perCapita', label: 'Per 1,000 residents' },
    { id: 'ev_map', value: 'map', label: 'Tract Map' }
  ];
  const allowedEvAdoptionOptions = evAdoptionOptions
    .filter((option) => isChartEnabled('ev_adoption', option.id))
    .sort((a, b) => getChartOrder('ev_adoption', a.id) - getChartOrder('ev_adoption', b.id));

  const evChargingOptions = [
    { id: 'ev_charging_people', value: 'peoplePerCharger', label: 'People per Charger' },
    { id: 'ev_charging_counts', value: 'counts', label: 'Total Chargers' },
    { id: 'ev_charging_evs', value: 'evsPerCharger', label: 'EVs per Charger' },
    { id: 'ev_charging_map', value: 'map', label: 'Tract Map' }
  ];
  const allowedEvChargingOptions = evChargingOptions
    .filter((option) => isChartEnabled('ev_charging', option.id))
    .sort((a, b) => getChartOrder('ev_charging', a.id) - getChartOrder('ev_charging', b.id));

  const transportMapVariables = [
    { id: 'ev_adoption', value: 'ev_adoption' },
    { id: 'total_chargers', value: 'total_chargers' },
    { id: 'vehicles_per_household', value: 'vehicles_per_household' },
    { id: 'zero_vehicle_households', value: 'zero_vehicle_households' },
    { id: 'commute_time', value: 'commute_time' }
  ];
  const allowedTransportMapVariables = transportMapVariables
    .filter((option) => isChartEnabled('transportation_maps', option.id))
    .sort((a, b) => getChartOrder('transportation_maps', a.id) - getChartOrder('transportation_maps', b.id))
    .map((option) => option.value);
  const transportMapsEnabled =
    isSectionEnabled('transportation_maps') && allowedTransportMapVariables.length > 0;


  React.useEffect(() => {
    if (allowedVmtOptions.length && !allowedVmtOptions.some((opt) => opt.value === vmtChartView)) {
      setVmtChartView(allowedVmtOptions[0].value);
    }
  }, [allowedVmtOptions, vmtChartView]);

  React.useEffect(() => {
    if (allowedEvAdoptionOptions.length && !allowedEvAdoptionOptions.some((opt) => opt.value === evAdoptionView)) {
      setEvAdoptionView(allowedEvAdoptionOptions[0].value as 'perCapita' | 'projection' | 'map');
    }
  }, [allowedEvAdoptionOptions, evAdoptionView]);

  React.useEffect(() => {
    if (allowedEvChargingOptions.length && !allowedEvChargingOptions.some((opt) => opt.value === evChargingView)) {
      setEvChargingView(allowedEvChargingOptions[0].value as 'counts' | 'peoplePerCharger' | 'evsPerCharger' | 'map');
    }
  }, [allowedEvChargingOptions, evChargingView]);

  const getSectionStyle = (sectionId: string) => ({
    order: getSectionOrder(sectionId),
    display: isSectionEnabled(sectionId) ? 'block' : 'none'
  });

  const toggleVMTView = (view: string) => {
    setActiveVMTView(view);
  };

  const toggleEVView = (view: string) => {
    setActiveEVView(view);
  };




  const handleVMTMetricsCalculated = useCallback((metrics: VMTMetrics) => {
    setVmtMetrics(metrics);
  }, []);

  // VMT per capita metrics handler removed - no longer needed

  const handleVMTDataCalculated = useCallback((data: any[]) => {
    console.log('🚗 Transportation page received VMT data:', data);
    console.log('🚗 VMT data length:', data.length);
    console.log('🚗 VMT data years:', data.map(d => d.year));
    
    // Set VMT data using the new service for GHG inventory
    setVMTDataFromTransportation(data);
    
    // Also set legacy window variable for backward compatibility
    if (typeof window !== 'undefined') {
      window.vmtChartData = data;
      console.log('🚗 VMT data set for GHG inventory (legacy):', data);
    }
  }, []);


  const handleTransportMapDataLoaded = useCallback((metrics: TransportMapMetrics) => {
    setTransportMapMetrics(metrics);
  }, []);

  if (!currentLocation) {
    return (
      <div>
        <PageHeader 
          title="Transportation & Mobility" 
          subtitle="Please select a location to view transportation data."
        />
      </div>
    );
  }

  if (coreDataLoading) {
    return (
      <div>
        <PageHeader 
          title="Loading core data..." 
          subtitle="Preloading VMT, electricity, and natural gas data for this location."
          variant="transportation"
        />
      </div>
    );
  }

  return (
    <div data-report-root="transportation">
      <PageHeader 
        title={`Transportation & Mobility - ${currentLocation}`}
        subtitle="Comprehensive transportation insights and mobility trends for the selected location."
        variant="transportation"
      />

      <div style={{ display: 'flex', flexDirection: 'column', gap: '32px' }}>
      <div style={getSectionStyle('vmt')}>
      {/* Vehicle Miles Traveled Section */}
      <ChartLayout
        className="vmt-compact"
        title="Vehicle Miles Traveled (VMT)"
        chartType="bar"
        keyMetrics={
          <KeyMetrics>
            <KeyMetric 
              value={vmtMetrics ? `${vmtMetrics.currentVMT}` : "Loading..."} 
              label="Average daily VMT per capita (2023)"
              color="#1e3a8a"
            />
            <KeyMetric 
              value={vmtMetrics ? `${vmtMetrics.changeSince2005 > 0 ? '+' : ''}${vmtMetrics.changeSince2005}%` : "Loading..."} 
              label="Change since 2005"
              color={(vmtMetrics ? vmtMetrics.changeSince2005 : 0) < 0 ? "#10b981" : "#ef4444"}
            />
            <KeyMetric 
              value={vmtMetrics ? `${vmtMetrics.californiaAverage}` : "Loading..."} 
              label="California average VMT"
              color="#f59e0b"
            />
          </KeyMetrics>
        }
        chartContent={
          vmtChartView === 'comparison' ? (
            <VMTComparisonChart
              location={currentLocation}
              geofips={selectedGeofips}
              onDownloadPNGReady={(handler) => setComparisonDownloadPNG(() => handler)}
            />
          ) : vmtChartView === 'emfac' ? (
            <EMFACCountyVMTChart
              location={currentLocation}
              geofips={selectedGeofips}
              onDownloadPNGReady={(handler) => setEmfacCountyDownloadPNG(() => handler)}
            />
          ) : vmtChartView === 'fleet' ? (
            <EMFACFleetComparisonChart
              location={currentLocation}
              geofips={selectedGeofips}
              onDownloadPNGReady={(handler) => setEmfacFleetDownloadPNG(() => handler)}
            />
          ) : (
            <VMTChart 
              location={currentLocation} 
              geofips={selectedGeofips || null} 
              chartView={vmtChartView as 'total' | 'percapita'} 
              emfac2005_2014Method={vmt2005_2014Method}
              backcastingMethod={vmtBackcastingMethod}
              emfacFleetVersion={vmtBackcastingMethod === 'EMFAC2021 Fleet' ? '2021' : '2025'}
              onMetricsCalculated={handleVMTMetricsCalculated} 
              onDataCalculated={handleVMTDataCalculated}
              onDataTableReady={(content, toggleFn) => {
                setVmtDataTableContent(content);
                // Store toggle function in a ref or state if needed
              }}
              onDownloadPNGReady={(handler) => setVmtDownloadPNG(() => handler)}
              onDownloadCSVReady={(handler) => setVmtDownloadCSV(() => handler)}
            />
          )
        }
        dataTableContent={vmtChartView !== 'comparison' && vmtChartView !== 'emfac' && vmtChartView !== 'fleet' ? vmtDataTableContent : undefined}
        showDataTable={vmtChartView !== 'comparison' && vmtChartView !== 'emfac' && vmtChartView !== 'fleet' ? vmtShowDataTable : false}
        onToggleDataTable={() => setVmtShowDataTable(!vmtShowDataTable)}
        onDownloadPNG={
          vmtChartView === 'comparison'
            ? comparisonDownloadPNG ?? undefined
            : vmtChartView === 'emfac'
            ? emfacCountyDownloadPNG ?? undefined
            : vmtChartView === 'fleet'
            ? emfacFleetDownloadPNG ?? undefined
            : vmtDownloadPNG ?? undefined
        }
        onDownloadCSV={vmtChartView !== 'comparison' && vmtChartView !== 'emfac' && vmtChartView !== 'fleet' && vmtDownloadCSV ? vmtDownloadCSV : undefined}
        pngFilename={`VMT_Chart_${vmtChartView === 'total' ? 'Total' : 'Per_Capita'}_${currentLocation.replace(/\s+/g, '_')}`}
        csvFilename={`VMT_Data_${vmtChartView === 'total' ? 'Total' : 'Per_Capita'}_${currentLocation.replace(/\s+/g, '_')}`}
        chartSelector={{
          options: allowedVmtOptions.map((option) => ({ value: option.value, label: option.label })),
          value: vmtChartView,
          onChange: setVmtChartView
        }}
        additionalButtonGroups={vmtChartView !== 'comparison' && vmtChartView !== 'emfac' && vmtChartView !== 'fleet' ? [
          {
            label: '(2005 - 2014)',
            options: [
              { value: 'Combined', label: 'Combined' },
              { value: 'EMFAC2025', label: 'EMFAC2025' },
              { value: 'Population', label: 'Population' }
            ],
            value: vmt2005_2014Method,
            onChange: (value) => setVmt2005_2014Method(value as 'EMFAC2025' | 'Population' | 'Combined')
          },
          {
            label: '(2015-2022)',
            options: [
              { value: 'Combined', label: 'Combined' },
              { value: 'AADT', label: 'AADT' },
              { value: 'EMFAC2025', label: 'EMFAC2025' },
              { value: 'EMFAC Fleet', label: 'EMFAC2025 Fleet' },
              { value: 'EMFAC2021 Fleet', label: 'EMFAC2021 Fleet' },
              { value: 'Population', label: 'Population' }
            ],
            value: vmtBackcastingMethod,
            onChange: (value) => setVmtBackcastingMethod(value as 'AADT' | 'EMFAC2025' | 'EMFAC Fleet' | 'EMFAC2021 Fleet' | 'Population' | 'Combined')
          }
        ] : undefined}
        overviewContent={
          vmtChartView === 'comparison' ? (
            <OverviewContent
              title="Light-Duty vs Heavy-Duty VMT Comparison"
              description="This chart compares light-duty and heavy-duty vehicle miles traveled, highlighting the different trends and contributions of each vehicle class."
            >
              <p>
                This comparison reveals the relative contributions of light-duty passenger vehicles versus heavy-duty commercial vehicles to total VMT in {currentLocation}. Understanding this distribution is important for targeting emission reduction strategies, as heavy-duty vehicles typically have higher emissions per mile but lower total miles traveled.
              </p>
            </OverviewContent>
          ) : vmtChartView === 'emfac' ? (
            <OverviewContent
              title="EMFAC County-Level LDV VMT Overview"
              description="This chart compares light-duty vehicle (LDV) miles traveled (VMT) data from EMFAC2021 (emfac2021_county) and EMFAC2025 (emfac2025_county) county-level models from 2000 to 2050."
            >
              <p>
                The chart shows LDV VMT data from both EMFAC2021 (legacy) and EMFAC2025 (current) models for {currentLocation ? `the county containing ${currentLocation}` : 'the selected county'}. 
                This allows for comparison between the two model versions and provides insight into how vehicle fleet data and projections have evolved. 
                The data is aggregated at the county level, which is the standard geography for EMFAC data, and spans from 2000 to 2050.
              </p>
            </OverviewContent>
          ) : vmtChartView === 'fleet' ? (
            <OverviewContent
              title="EMFAC Fleet-Level LDV VMT Overview"
              description="This chart compares light-duty vehicle (LDV) miles traveled (VMT) data from EMFAC2021 Fleet (emfac2021_fleet_mat) and EMFAC2025 Fleet (emfac2025_fleet_mat) city-level models from 2015 to 2023."
            >
              <p>
                The chart shows LDV VMT data from both EMFAC2021 Fleet and EMFAC2025 Fleet models for {currentLocation}. 
                This allows for comparison between the two fleet-based model versions at the city level. 
                The data is aggregated from block group-level fleet data and provides city-specific VMT estimates from 2015 to 2023.
              </p>
            </OverviewContent>
          ) : (
            <OverviewContent
              title="VMT Overview"
              description="Vehicle Miles Traveled (VMT) captures the total distance traveled by all vehicles in a community over a given period of time,
              serving as a key measure of driving activity. Statewide, VMT increased slightly year over year before declining during the COVID-19 pandemic
              as a result of stay-at-home orders and transitions to remote work. Since then, VMT has been rising at a steady pace, although not quite reaching
              peak levels prior to the pandemic."
            >
              <p>
                {vmtMetrics ? (
                  <>
                    Light-Duty vehicle miles traveled (VMT) in {currentLocation} totalled {(vmtMetrics.totalVMT2023 / 1000000).toFixed(2)} million miles in 2023 according to Replica's statewide origin-destination model. This represents a {vmtMetrics.changeSince2005 > 0 ? 'increase' : 'decrease'} of {Math.abs(vmtMetrics.changeSince2005)}% since 2005, based on changes in local traffic counts, vehicle registrations and population. 
                    {vmtMetrics.changeSince2005 < 0 ? 'This reduction reflects successful transportation demand management strategies and shifts toward more sustainable mobility options. ' : 'This increase indicates growing transportation demand that may require additional climate action measures. '} 
                    The current VMT is {vmtMetrics.currentVMT > vmtMetrics.californiaAverage ? 'above' : 'below'} the California average of {vmtMetrics.californiaAverage} miles per capita, 
                    {vmtMetrics.currentVMT > vmtMetrics.californiaAverage ? 'indicating opportunities for further improvement.' : 'showing positive performance compared to the state average.'}
                  </>
                ) : (
                  `Loading VMT data for ${currentLocation}...`
                )}
              </p>
            </OverviewContent>
          )
        }
        relevanceContent={
          vmtChartView === 'fleet' ? (
            <RelevanceContent
              title="Policy Relevance"
              description="EMFAC Fleet-level VMT data provides city-specific estimates of vehicle activity based on fleet composition, which can be used for emissions calculations and policy planning at the local level."
            >
              <div className="methodology-details-simple">
                <p>Fleet-level VMT data is particularly useful for cities developing climate action plans, as it provides location-specific estimates that reflect local vehicle fleet characteristics and usage patterns.</p>
              </div>
            </RelevanceContent>
          ) : vmtChartView === 'emfac' ? (
            <RelevanceContent
              title="Policy Relevance"
              description="EMFAC county-level VMT data provides standardized, model-based estimates of vehicle activity that can be used for emissions calculations and policy planning."
            >
              <div className="methodology-details-simple">
                <h5>Emissions Calculations</h5>
                <p>EMFAC VMT data is used as a key input for calculating transportation sector greenhouse gas emissions, providing standardized estimates across all California counties.</p>
              </div>
              <div className="methodology-details-simple">
                <h5>Model Comparison</h5>
                <p>Comparing EMFAC2021 and EMFAC2025 data helps identify how model updates affect VMT estimates and projections, which is important for understanding the evolution of transportation data.</p>
              </div>
            </RelevanceContent>
          ) : (
            <RelevanceContent
              title="Policy Relevance"
              description="Vehicle miles traveled (VMT) are the primary activity indicator for the transportation sector. Light-duty motor vehicles contribute nearly 40% of statewide emissions."
            >
              <div className="methodology-details-simple">
                <h5>Climate Action Planning</h5>
                <p>Local governments seek to limit VMT through housing densification, land use policies, public transit, biking infrastructure, micro mobility and other means. Despite these efforts, total VMT and VMT per capita continue to rise statewide.</p>
              </div>
              <div className="methodology-details-simple">
                <h5>Quality of Life Indicators</h5>
                <p>Reduced VMT may indicate that people are able to access their daily needs without traveling as far, or by active mobility or transit options, which are indicators of better quality of life.</p>
              </div>
            </RelevanceContent>
          )
        }
        equityContent={
          vmtChartView === 'fleet' ? (
            <EquityContent
              title="Equity Considerations"
              description="Fleet-level VMT data provides city-specific estimates that better reflect local transportation patterns, though variations within cities may still exist."
            >
              <div className="methodology-details-simple">
                <p>City-level fleet data allows for more targeted policy interventions that can address local transportation equity concerns more effectively than county-level aggregates.</p>
              </div>
            </EquityContent>
          ) : vmtChartView === 'emfac' ? (
            <EquityContent
              title="Equity Considerations"
              description="County-level VMT data represents aggregate vehicle activity across all communities within a county, which may mask local variations in transportation patterns."
            >
              <div className="methodology-details-simple">
                <h5>Geographic Aggregation</h5>
                <p>County-level data provides a regional view but may not reflect the specific transportation patterns of individual cities or neighborhoods within the county.</p>
              </div>
            </EquityContent>
          ) : (
            <EquityContent
              title="Equity Considerations"
              description={"Younger and low-income residents are less likely to own motor vehicles and contribute less to vehicle miles traveled, on average.\n              However, many low-income communities exist in close proximity to highways and major roads, disproportionately burdening them with the environmental\n              and health impacts of motor vehicle travel. "}
            >
              <div className="methodology-details-simple">
                <h5>Access and Distance Challenges</h5>
                <p> Higher housing prices in urban centers relative to suburban or exurban areas leads many working adults to travel long distances to reach workplaces,
                  shopping, and other destinations. This often disproportionately burdens low-income communities with longer motor vehicle travel times.
                  Housing affordability and accessibility, especially in high demand, job-rich areas, is a key factor in reducing VMT.</p>
              </div>
              <div className="methodology-details-simple">
                <h5>Community Design Impact</h5>
                <p>Higher VMT can also indicate a lack of amenities nearby. Designing mixed-use communities with jobs, schools, shopping, and recreation nearby
                   can reduce VMT by shifting vehicle use to shorter, local trips or encourage active transportation options like walking and biking.</p>
              </div>
            </EquityContent>
          )
        }
        methodologyContent={
          vmtChartView === 'comparison' ? (
            <MethodologyContent
              title="Methodology"
              description="VMT Comparison chart shows light-duty and heavy-duty vehicle miles traveled separately, using city-specific data from emfac2025_fleet for recent years and EMFAC2025 backcasting for historical data."
              dataSources={[
                'emfac2025_fleet (city-specific EMFAC2025 data)',
                'emfac2021_county_filtered (county-level backcasting ratios)',
                'VMT chart data (for LDV consistency)'
              ]}
              methodology={[
                'Light-Duty Vehicle (LDV) VMT: Uses the same calculation as the main VMT chart, including all backcasting logic, to ensure consistency across the application.',
                'Heavy-Duty Vehicle (HDV) VMT for 2015-2023: Sums all total_vmt values from emfac2025_fleet where class=HDV for the selected location (city geofips), providing city-specific HDV data.',
                'Heavy-Duty Vehicle (HDV) VMT for 2005-2014: Uses county-level backcasting ratios from emfac2021_county_filtered, where the 2015 HDV VMT baseline is scaled by pre-calculated backcasting_ratio values for each year.',
                'HDV backcasting applies the county ratio to city data, ensuring historical trends match broader regional patterns while maintaining city-specific values for the baseline year (2015).',
                'This approach captures the fact that HDV VMT data is only available at city-level from 2015 onwards in emfac2025_fleet, requiring backcasting for earlier years.'
              ]}
            />
          ) : vmtChartView === 'fleet' ? (
            <MethodologyContent
              title="Methodology"
              description="This chart displays LDV VMT data directly from EMFAC2021 Fleet (emfac2021_fleet_mat) and EMFAC2025 Fleet (emfac2025_fleet_mat) city-level tables, comparing the two fleet-based model versions from 2015 to 2023."
              dataSources={[
                'emfac2021_fleet_mat (EMFAC2021 Fleet city-level data)',
                'emfac2025_fleet_mat (EMFAC2025 Fleet city-level data)',
              ]}
              methodology={[
                'Data is aggregated from block group-level fleet data to city-level totals',
                'LDV VMT is summed across all fuel types and vehicle categories for each year',
                'The chart compares EMFAC2021 Fleet and EMFAC2025 Fleet side-by-side to show differences between model versions',
                'Data is limited to 2015-2023 as fleet-level data is only available for these years',
              ]}
            />
          ) : vmtChartView === 'emfac' ? (
            <MethodologyContent
              title="Methodology"
              description="This chart displays LDV VMT data directly from EMFAC2021 and EMFAC2025 county-level tables, comparing the two model versions from 2000 to 2050."
              dataSources={[
                'emfac2021_county (EMFAC2021 county-level data)',
                'emfac2025_county (EMFAC2025 county-level data)'
              ]}
              methodology={[
                'Data is queried directly from emfac2021_county and emfac2025_county tables for the county containing the selected location.',
                'For city locations, the chart automatically identifies and displays data for the containing county.',
                'Only light-duty vehicle (LDV) data is displayed, filtered by class = "LDV".',
                'VMT values are aggregated by year across all vehicle categories and fuel types for LDV only.',
                'The chart compares EMFAC2021 and EMFAC2025 LDV VMT side-by-side to show differences between model versions.',
                'Data spans from 2000 to 2050, covering historical years, current estimates, and long-term projections.'
              ]}
            />
          ) : (
            <MethodologyContent
              title="Methodology"
              description="VMT from 2005 to 2023 is built in three segments: backcasting for 2005-2014, backcasting for 2015-2022, and Replica for 2023. By default, multiple sources are averaged (Combined); users can instead select a single backcasting source from the dropdown menus."
              dataSources={[
                'EMFAC2025 / EMFAC2021 (county and fleet)',
                'CARB Fleet Database',
                'CalTrans (AADT)',
                'Replica',
                'US Census (Population)'
              ]}
              methodology={[
                'By default, multiple sources are averaged for backcasting. Users can alternatively select a single available source using the dropdown menus: one for 2005-2014 (EMFAC2025, Population, or Combined) and one for 2015-2022 (AADT, EMFAC2025, EMFAC Fleet, EMFAC2021 Fleet, Population, or Combined).',
                '2005-2014: Backcasting uses year-over-year scaling from a 2015 baseline. Combined (default) averages ratios from EMFAC2025 county-level VMT and US Census population; single-source options are EMFAC2025 or Population.',
                '2015-2022: Backcasting applies scaling from the 2023 baseline. Combined (default) averages ratios from CalTrans AADT, EMFAC2025 county, EMFAC Fleet (2021 and 2025), and Population where available; single-source options are AADT, EMFAC2025, EMFAC Fleet, EMFAC2021 Fleet, or Population. CalTrans monitors are filtered for complete data 2015-2022.',
                '2023-2025: VMT is from Replica\'s origin-destination travel demand model. The estimate includes 100% of trips that occur entirely within the geographic boundary and 50% of trips that start or end within the boundary.',
                'Together, the backcasting sources capture changes in local traffic (AADT), vehicle ownership (CARB Fleet), and population, providing a consistent measurement of local VMT over time.'
              ]}
            />
          )
        }
        activeView={activeVMTView}
        onViewChange={toggleVMTView}
        showRelevanceEquity={true}
        enableComments={true}
        location={currentLocation}
        overviewSectionId="transportation"
        overviewChartId="vehicle_miles"
        registerPng={false}
      />
      </div>

      {/* Means of Transportation to Work */}
      <div style={getSectionStyle('means_of_transportation')}>
        <MeansOfTransportation 
          location={currentLocation}
          geofips={selectedGeofips}
        />
      </div>

      {/* Electric Vehicle Adoption Section */}
      <div style={getSectionStyle('ev_adoption')}>
      <ChartLayout
        title="Electric Vehicle Adoption"
        chartType="line"
        chartSelector={{
          options: allowedEvAdoptionOptions.map((option) => ({ value: option.value, label: option.label })),
          value: evAdoptionView,
          onChange: (value: string) => setEvAdoptionView(value as 'perCapita' | 'projection' | 'map')
        }}
        keyMetrics={
          <KeyMetrics>
            <KeyMetric 
              value="8.7" 
              label="EVs per 1,000 residents"
              color="#1e3a8a"
            />
            <KeyMetric 
              value="+156%" 
              label="Growth since 2020"
              color="#10b981"
            />
            <KeyMetric 
              value="127" 
              label="Public charging stations"
              color="#f59e0b"
            />
          </KeyMetrics>
        }
        chartContent={
          evAdoptionView === 'map' ? (
            <GenericMapTiered 
              location={currentLocation}
              hideVariableSelector={true}
              lockedVariable="ev_adoption"
              onDataLoaded={(metrics) => {
                // Update key metrics if needed when map data loads
                if (metrics && metrics.variable === 'ev_adoption') {
                  // Could update evOverview with map-derived metrics if needed
                }
              }}
            />
          ) : (
            <div id="ev-adoption-chart">
              <EVFleetAdoption 
                location={currentLocation} 
                geofips={selectedGeofips} 
                activeTab={evAdoptionView}
                onTabChange={setEvAdoptionView}
                onMetricsCalculated={setEvOverview} 
              />
            </div>
          )
        }
        overviewContent={
          <OverviewContent
            title="EV Adoption Overview"
            description="This chart displays electric vehicle adoption trends, a critical indicator of transportation electrification progress."
          >
            <p>
              {evOverview ? (
                <>
                  In {evOverview.observedYear}, {currentLocation} had {evOverview.observedTotal.toLocaleString()} light-duty electric vehicles and is on pace to reach {Math.round(evOverview.projected2030).toLocaleString()} by 2030. To meet California's target of 5 million electric vehicles by 2030, {currentLocation} would need approximately {Math.round(evOverview.target2030).toLocaleString()} vehicles. {currentLocation} {evOverview.onPace ? 'will surpass this target if growth continues at the current rate.' : 'is not yet on pace and will need to accelerate adoption to meet the target.'}
                </>
              ) : (
                `Loading EV adoption overview for ${currentLocation}...`
              )}
            </p>
          </OverviewContent>
        }
        relevanceContent={
          <RelevanceContent
            title="Relevance to Climate Action"
            description="Electric vehicle adoption is essential for reducing transportation emissions and achieving climate goals."
          >
            <div className="methodology-details-simple">
              <h5>Emissions Reduction</h5>
              <p>EVs produce significantly fewer emissions than gasoline vehicles, especially when powered by renewable electricity.</p>
            </div>
            <div className="methodology-details-simple">
              <h5>Infrastructure Planning</h5>
              <p>EV adoption rates guide charging infrastructure investment and grid capacity planning for transportation electrification.</p>
            </div>
          </RelevanceContent>
        }
        equityContent={
          <EquityContent
            title="Equity Considerations"
            description="Access to EVs remains uneven across income, race, and geography. Equity-centered programs are necessary to ensure all Californians benefit."
          >
            <div className="methodology-details-simple">
              <h5>Affordability & Tenure</h5>
              <p>Lower-income households and renters face high upfront costs and limited home charging. Used-EV rebates and low‑income financing expand access.</p>
            </div>
            <div className="methodology-details-simple">
              <h5>Charging for Renters</h5>
              <p>Mandates and incentives for multifamily and renter access to charging reduce inequities in home charging availability.</p>
            </div>
            <div className="methodology-details-simple">
              <h5>Targeted Community Grants</h5>
              <p>Place‑based grants and community charging projects support adoption in underserved neighborhoods.</p>
            </div>
          </EquityContent>
        }
        methodologyContent={
          <MethodologyContent
            title="Methodology"
            description="Data source and roll‑up approach for EV adoption metrics."
            dataSources={[
              'EMFAC Fleet Database (CARB) built from DMV vehicle registrations',
              'Population data for per‑capita calculations'
            ]}
            methodology={[
              'We query EV fleet (fuel = Electric and PluginHybrid, class = LDV) at tract level rolled up to cities/counties using a population‑weighted correspondence table.',
              'Per‑capita chart shows EVs per 1,000 residents; statewide (geofips=6) shown for comparison.',
              'Projections, where shown, consider the 5‑year average annual growth in EV registrations.'
            ]}
          />
        }
        activeView={activeEVView}
        onViewChange={toggleEVView}
        showRelevanceEquity={true}
        enableComments={true}
        location={currentLocation}
        overviewSectionId="transportation"
        overviewChartId="ev_fleet_adoption"
        onDownloadPNG={() => {
          if (evAdoptionView === 'map') {
            // Map download would need to be handled by GenericMapTiered component
            console.log('Map PNG download - to be implemented in GenericMapTiered');
            return;
          }
          const canvas = document.querySelector('#ev-adoption-chart canvas') as HTMLCanvasElement;
          if (canvas) {
            const link = document.createElement('a');
            const viewLabel = evAdoptionView === 'perCapita' ? 'Per_Capita' : 'Projection';
            link.download = `EV_Adoption_${viewLabel}_${currentLocation.replace(/\s+/g, '_')}.png`;
            link.href = canvas.toDataURL();
            link.click();
          }
        }}
        onDownloadCSV={() => {
          // This would need to be implemented based on the chart data
          const viewLabel = evAdoptionView === 'perCapita' ? 'Per_Capita' : evAdoptionView === 'map' ? 'Tract_Map' : 'Projection';
          console.log(`CSV download for EV Adoption ${viewLabel} not yet implemented`);
        }}
        pngFilename={`EV_Adoption_${evAdoptionView === 'perCapita' ? 'Per_Capita' : evAdoptionView === 'map' ? 'Tract_Map' : 'Projection'}_${currentLocation.replace(/\s+/g, '_')}`}
        csvFilename={`EV_Adoption_${evAdoptionView === 'perCapita' ? 'Per_Capita' : evAdoptionView === 'map' ? 'Tract_Map' : 'Projection'}_Data_${currentLocation.replace(/\s+/g, '_')}`}
        registerPng={false}
      />
      </div>

      

      {/* EV Charging Infrastructure Section */}
      <div style={getSectionStyle('ev_charging')}>
      <ChartLayout
        title="EV Charging Infrastructure"
        description="Current charging station infrastructure compared to 2030 targets and accessibility metrics."
        chartType="bar"
        chartContent={
          evChargingView === 'map' ? (
            <GenericMapTiered 
              location={currentLocation}
              hideVariableSelector={true}
              lockedVariable="total_chargers"
              onDataLoaded={(metrics) => {
                // Update metrics if needed when map data loads
                if (metrics && metrics.variable === 'total_chargers') {
                  // Could update charging metrics if needed
                }
              }}
            />
          ) : (
            <div id="ev-charging-chart">
              <EVChargingInfrastructure location={currentLocation} activeTab={evChargingView} onTabChange={setEvChargingView} />
            </div>
          )
        }
        chartSelector={{
          options: allowedEvChargingOptions.map((option) => ({ value: option.value, label: option.label })),
          value: evChargingView,
          onChange: (value: string) => setEvChargingView(value as 'counts' | 'peoplePerCharger' | 'evsPerCharger' | 'map')
        }}
        activeView={activeChargingView}
        onViewChange={setActiveChargingView}
        showRelevanceEquity={true}
        enableComments={true}
        location={currentLocation}
        layout="side-by-side"
        onDownloadPNG={() => {
          if (evChargingView === 'map') {
            // Map download would need to be handled by GenericMapTiered component
            console.log('Map PNG download - to be implemented in GenericMapTiered');
            return;
          }
          const canvas = document.querySelector('#ev-charging-chart canvas') as HTMLCanvasElement;
          if (canvas) {
            const link = document.createElement('a');
            const viewLabel = evChargingView === 'counts' ? 'Total_Chargers' : evChargingView === 'peoplePerCharger' ? 'People_Per_Charger' : 'EVs_Per_Charger';
            link.download = `EV_Charging_${viewLabel}_${currentLocation.replace(/\s+/g, '_')}.png`;
            link.href = canvas.toDataURL();
            link.click();
          }
        }}
        onDownloadCSV={() => {
          // This would need to be implemented based on the chart data
          const viewLabel = evChargingView === 'counts' ? 'Total_Chargers' : evChargingView === 'peoplePerCharger' ? 'People_Per_Charger' : 'EVs_Per_Charger';
          console.log(`CSV download for EV Charging ${viewLabel} not yet implemented`);
        }}
        pngFilename={`EV_Charging_${evChargingView === 'counts' ? 'Total_Chargers' : evChargingView === 'peoplePerCharger' ? 'People_Per_Charger' : evChargingView === 'evsPerCharger' ? 'EVs_Per_Charger' : 'Tract_Map'}_${currentLocation.replace(/\s+/g, '_')}`}
        csvFilename={`EV_Charging_${evChargingView === 'counts' ? 'Total_Chargers' : evChargingView === 'peoplePerCharger' ? 'People_Per_Charger' : evChargingView === 'evsPerCharger' ? 'EVs_Per_Charger' : 'Tract_Map'}_Data_${currentLocation.replace(/\s+/g, '_')}`}
        registerPng={false}
        overviewContent={
          <OverviewContent
            title="EV Charging Infrastructure Overview"
            description="This chart displays electric vehicle charging infrastructure data, comparing current capacity to 2030 targets."
          >
            <p>
              Electric vehicle charging infrastructure is a critical component of California's clean transportation transition. 
              This analysis examines the current state of EV charging infrastructure in {currentLocation} and compares it to 
              statewide targets and benchmarks.
            </p>
            <p>
              The data shows current charging station counts, including Level 2 chargers for daily charging and DC Fast 
              chargers for long-distance travel. We also analyze charging infrastructure density through metrics like 
              people per charger and EVs per charger to understand accessibility and utilization.
            </p>
            <p>
              California has set ambitious goals for EV adoption, with targets to reach 1 million charging stations by 2030. 
              Understanding current infrastructure gaps and planning for equitable distribution is essential for achieving 
              these goals while ensuring all communities benefit from the clean transportation transition.
            </p>
          </OverviewContent>
        }
        relevanceContent={
          <RelevanceContent
            title="Policy Relevance"
            description="Why EV charging infrastructure matters for climate action and local planning."
          >
            <p>
              <strong>Policy Relevance:</strong> EV charging infrastructure is directly tied to California's climate goals 
              and transportation electrification policies. The state's commitment to reach carbon neutrality by 2045 
              requires a massive expansion of clean transportation infrastructure.
            </p>
            <p>
              <strong>Local Planning:</strong> Cities and counties need to understand their current charging infrastructure 
              status to plan for future growth, allocate resources effectively, and ensure equitable access to clean 
              transportation options for all residents.
            </p>
            <p>
              <strong>Economic Development:</strong> Charging infrastructure supports local economic development by 
              attracting EV drivers, supporting local businesses, and creating jobs in the clean energy sector.
            </p>
          </RelevanceContent>
        }
        equityContent={
          <EquityContent
            title="Equity Considerations"
            description="Ensuring equitable access to EV charging infrastructure across all communities."
          >
            <p>
              <strong>Accessibility Concerns:</strong> EV charging infrastructure must be distributed equitably to ensure 
              all communities can participate in the clean transportation transition. Historically underserved communities 
              may face barriers to EV adoption without adequate charging infrastructure.
            </p>
            <p>
              <strong>Multi-Family Housing:</strong> Residents of apartments and condominiums often lack access to home 
              charging, making public charging infrastructure even more critical for these communities.
            </p>
            <p>
              <strong>Rural and Underserved Areas:</strong> Ensuring charging infrastructure reaches rural communities and 
              areas with limited transportation options is essential for statewide equity in clean transportation access.
            </p>
          </EquityContent>
        }
        methodologyContent={
          <MethodologyContent
            title="Data Sources & Methodology"
            description="Detailed information about data sources and calculation methods for EV charging infrastructure analysis."
            dataSources={[
              'U.S. Department of Energy, Alternative Fuels Data Center (AFDC) Station Locator',
              'U.S. Census Bureau American Community Survey (ACS) 5-Year Estimates for 2023',
              'California Air Resources Board (CARB) EMFAC2021 model and CARB Fleet Database for 2023'
            ]}
            methodology={[
              'Charging station counts are aggregated by geographic location from AFDC database',
              'Data includes public and private charging stations with status: Available (E), Planned (P), and Temporarily Unavailable (T)',
              'Per capita calculations use 2023 population estimates from ACS',
              'EV counts include both Battery Electric Vehicles (BEVs) and Plug-in Hybrid Electric Vehicles (PHEVs)',
              'County-level data is used for comparison when city-level data is not available',
              '2030 targets based on California\'s goal to reach 1 million charging stations statewide'
            ]}
          />
        }
      />
      </div>

      {/* EV Adoption Barriers by Income Quintile */}
      <div style={getSectionStyle('ev_adoption_barriers')}>
        <EVAdoptionBarriersChart location={currentLocation} />
      </div>

      {/* Transportation-Related Maps */}
      <div
        style={{
          order: getSectionOrder('transportation_maps'),
          display: transportMapsEnabled ? 'block' : 'none'
        }}
      >
      <ChartLayout
        title="Transportation-Related Maps"
        description="Interactive maps for transportation indicators such as EV adoption, charging infrastructure, and vehicle access."
        chartType="map"
        layout="vertical"
        keyMetrics={
          transportMapMetrics ? (
            <KeyMetrics>
              <KeyMetric
                value={
                  transportMapMetrics.variable === 'ev_adoption'
                    ? `${transportMapMetrics.currentValue.toFixed(1)} EVs/100`
                    : transportMapMetrics.variable === 'total_chargers'
                    ? `${Math.round(transportMapMetrics.currentValue).toLocaleString()} chargers`
                    : transportMapMetrics.variable === 'vehicles_per_household'
                    ? `${transportMapMetrics.currentValue.toFixed(2)} vehicles/HH`
                    : transportMapMetrics.variable === 'zero_vehicle_households'
                    ? `${Math.round(transportMapMetrics.currentValue).toLocaleString()} zero-vehicle HHs`
                    : transportMapMetrics.variable === 'commute_time'
                    ? `${transportMapMetrics.currentValue.toFixed(1)} min`
                    : `${Math.round(transportMapMetrics.currentValue).toLocaleString()}`
                }
                label={
                  transportMapMetrics.variable === 'ev_adoption'
                    ? 'EVs per 100 People'
                    : transportMapMetrics.variable === 'total_chargers'
                    ? 'Total EV Chargers'
                    : transportMapMetrics.variable === 'vehicles_per_household'
                    ? 'Vehicles per Household'
                    : transportMapMetrics.variable === 'zero_vehicle_households'
                    ? 'Zero Vehicle Households (est.)'
                    : transportMapMetrics.variable === 'commute_time'
                    ? 'Average Commute Time'
                    : 'Value'
                }
              />
              <KeyMetric
                value={
                  transportMapMetrics.variable === 'ev_adoption'
                    ? `${transportMapMetrics.californiaAverage.toFixed(1)} EVs/100`
                    : transportMapMetrics.variable === 'total_chargers'
                    ? `${Math.round(transportMapMetrics.californiaAverage).toLocaleString()} chargers`
                    : transportMapMetrics.variable === 'vehicles_per_household'
                    ? `${transportMapMetrics.californiaAverage.toFixed(2)} vehicles/HH`
                    : transportMapMetrics.variable === 'zero_vehicle_households'
                    ? `${Math.round(transportMapMetrics.californiaAverage).toLocaleString()} zero-vehicle HHs`
                    : transportMapMetrics.variable === 'commute_time'
                    ? `${transportMapMetrics.californiaAverage.toFixed(1)} min`
                    : `${Math.round(transportMapMetrics.californiaAverage).toLocaleString()}`
                }
                label="California Average"
              />
            </KeyMetrics>
          ) : undefined
        }
        chartContent={
          <div id="transportation-maps-chart">
            <GenericMapTiered
              location={currentLocation}
              onDataLoaded={handleTransportMapDataLoaded}
              allowedVariables={allowedTransportMapVariables}
              enableScopeSelector={true}
            />
          </div>
        }
        onDownloadPNG={() => {
          const container = document.querySelector('#transportation-maps-chart');
          const canvas = container?.querySelector('canvas');
          if (canvas) {
            const link = document.createElement('a');
            link.download = `Transportation_Maps_${currentLocation.replace(/\s+/g, '_')}.png`;
            link.href = canvas.toDataURL('image/png');
            link.click();
          }
        }}
        overviewContent={
          transportMapMetrics ? (
            <OverviewContent
              title={
                transportMapMetrics.variable === 'ev_adoption'
                  ? 'Electric Vehicle Adoption Map Overview'
                  : transportMapMetrics.variable === 'total_chargers'
                  ? 'EV Charging Infrastructure Map Overview'
                  : transportMapMetrics.variable === 'vehicles_per_household'
                  ? 'Vehicles per Household Map Overview'
                  : transportMapMetrics.variable === 'zero_vehicle_households'
                  ? 'Zero Vehicle Households Map Overview'
                  : 'Commute Time Map Overview'
              }
              description={`This map visualizes transportation-related patterns across census tracts in ${currentLocation}, helping identify spatial variation in key indicators.`}
            >
              {transportMapMetrics.variable === 'ev_adoption' ? [
                `${currentLocation} has ${transportMapMetrics.currentValue.toFixed(1)} electric vehicles per 100 people, which is ${((transportMapMetrics.currentValue / transportMapMetrics.californiaAverage) * 100).toFixed(1)}% of the California average of ${transportMapMetrics.californiaAverage.toFixed(1)} EVs per 100 people.`,
                'Tracts with higher EV adoption often coincide with higher incomes, access to home charging, and early adoption of clean technologies.'
              ] : transportMapMetrics.variable === 'total_chargers' ? [
                `${currentLocation} has ${Math.round(transportMapMetrics.currentValue).toLocaleString()} total EV chargers, compared to a statewide average of ${Math.round(transportMapMetrics.californiaAverage).toLocaleString()} chargers.`,
                'Concentrations of chargers highlight areas with better EV accessibility, while gaps in coverage can indicate equity and infrastructure planning needs.'
              ] : transportMapMetrics.variable === 'vehicles_per_household' ? [
                `${currentLocation} has ${transportMapMetrics.currentValue.toFixed(2)} vehicles per household, which is ${((transportMapMetrics.currentValue / transportMapMetrics.californiaAverage) * 100).toFixed(1)}% of the California average of ${transportMapMetrics.californiaAverage.toFixed(2)} vehicles per household.`,
                'Higher vehicles per household generally signal greater auto dependence and higher transportation emissions per household.'
              ] : transportMapMetrics.variable === 'zero_vehicle_households' ? [
                `Estimated zero-vehicle households in ${currentLocation} total ${Math.round(transportMapMetrics.currentValue).toLocaleString()}, compared to a statewide average of ${Math.round(transportMapMetrics.californiaAverage).toLocaleString()}.`,
                'Tracts with higher shares of zero-vehicle households are especially sensitive to transit service quality and walkability.'
              ] : [
                `${currentLocation} has an average commute time of ${transportMapMetrics.currentValue.toFixed(1)} minutes, which is ${((transportMapMetrics.currentValue / transportMapMetrics.californiaAverage) * 100).toFixed(1)}% of the California average of ${transportMapMetrics.californiaAverage.toFixed(1)} minutes.`,
                'Longer commute times can indicate jobs-housing imbalances, limited transit options, or long-distance driving patterns.'
              ]}
            </OverviewContent>
          ) : undefined
        }
        relevanceContent={
          <RelevanceContent
            title="Relevance to Climate Action"
            description="Transportation-related maps help identify spatial patterns in vehicle dependence, EV readiness, and access to alternatives."
          >
            <div className="methodology-details-simple">
              <h5>Targeting Clean Transportation Investments</h5>
              <p>Maps of EV adoption, charging infrastructure, and vehicle access can guide where to prioritize transit, EV incentives, and active transportation projects.</p>
            </div>
            <div className="methodology-details-simple">
              <h5>Reducing VMT and Emissions</h5>
              <p>Areas with high vehicle ownership and long commutes are prime candidates for transit-oriented development and mode shift strategies.</p>
            </div>
          </RelevanceContent>
        }
        equityContent={
          <EquityContent
            title="Equity Considerations"
            description="Spatial patterns in transportation access are closely tied to income, race, and housing tenure."
          >
            <div className="methodology-details-simple">
              <h5>Access to Clean Mobility</h5>
              <p>Neighborhoods with low EV adoption and limited charging may require targeted programs to ensure equitable access to clean transportation options.</p>
            </div>
            <div className="methodology-details-simple">
              <h5>Transit and Walkability Gaps</h5>
              <p>Areas with many zero-vehicle households but poor transit or pedestrian infrastructure face disproportionate mobility challenges.</p>
            </div>
          </EquityContent>
        }
        methodologyContent={
          <MethodologyContent
            title="Methodology"
            description="Transportation map layers are based on tract-level datasets joined to cities and counties using correspondence tables."
            dataSources={[
              'ev_by_tract_2023 (EV adoption, charging infrastructure, vehicles per household, zero-vehicle households, population, commute time)',
              'Population and housing data from the American Community Survey 5-Year Estimates',
              'Regions table for 10-region grouping used in the Multifamily Energy Efficiency Targeting Tool'
            ]}
            methodology={[
              'Tract-level indicators are aggregated and summarized for the selected location and scope (county, region, or California).',
              'EV adoption and charging infrastructure metrics are derived from registration and station datasets joined to census tracts.',
              'Zero-vehicle households are estimated from percentages and average household size, using tract-level population.',
              'Commute time data comes from ACS travel-to-work tables and is averaged across tracts in the selected geography.'
            ]}
          />
        }
        activeView={activeTransportMapView}
        onViewChange={(view: string) =>
          setActiveTransportMapView(view as 'overview' | 'relevance' | 'equity' | 'methodology')
        }
        showRelevanceEquity={true}
        enableComments={true}
        location={currentLocation}
        registerPng={false}
      />
      </div>
      </div>

      <CustomSectionsForPage pageId="transportation" locationName={currentLocation} />
      <CustomDatasetsForPage pageId="transportation" locationName={currentLocation} />
    </div>
  );
};
